import { SUB_AGENCY_SORT_KEYS, type SubAgencyListItem, type SubAgencySortKey, organizationTypeFilterParam } from "@seagames/contracts";
import { type ColumnDef, DataTable, ListHeader, ListTag, ListToolbar, PrimaryCell, StatsStrip, useUiStrings } from "@seagames/ui";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { ConfirmDeleteModal } from "../../components/common/confirm-delete-modal";
import { EditDeleteActions } from "../../components/common/edit-delete-actions";
import { timestampColumn, usageColumn } from "../../components/common/list-columns";
import { type SubAgencyFormTarget, SubAgencyFormModal } from "../../components/sub-agencies/sub-agency-form-modal";
import { listSearch } from "../../lib/list-page/list-search";
import { useConfirmDelete } from "../../lib/list-page/use-confirm-delete";
import { useListPage } from "../../lib/list-page/use-list-page";
import { organizationTypeChipOptions } from "../../lib/organization-types/chips";
import { organizationTypeOptionsOptions } from "../../lib/organization-types/queries";
import { deleteSubAgency, invalidateSubAgencies, subAgenciesListOptions, subAgencyStatsOptions } from "../../lib/sub-agencies/queries";

export const Route = createFileRoute("/_app/settings/sub-agencies")({
	validateSearch: listSearch(SUB_AGENCY_SORT_KEYS, { organization_type_id: organizationTypeFilterParam }),
	component: SubAgenciesPage,
});

/** The filters as the URL holds them. `""` = not set; the URL drops empties. */
interface SubAgencyFilters extends Record<string, unknown> {
	search: string;
	/** A type's uuid, or `none` for sub-agencies with no type. */
	organizationTypeId: string;
}
const EMPTY_FILTERS: SubAgencyFilters = { search: "", organizationTypeId: "" };

function SubAgenciesPage() {
	const { t } = useTranslation("subAgencies");
	const { t: tc } = useTranslation("common");
	const { formatNumber } = useUiStrings();
	const search = Route.useSearch();
	const { session } = Route.useRouteContext();
	const isSuper = session.active_role === "SUPER_ADMIN";
	const [form, setForm] = useState<SubAgencyFormTarget | null>(null);

	const page = useListPage({
		search,
		navigate: Route.useNavigate(),
		listOptions: (q) => subAgenciesListOptions({ ...q, organization_type_id: search.organization_type_id }),
		columnsKey: "sub-agencies",
		filters: {
			empty: EMPTY_FILTERS,
			fromUrl: (s): SubAgencyFilters => ({ search: s.search ?? "", organizationTypeId: s.organization_type_id ?? "" }),
			toUrl: (f) => ({ search: f.search.trim() || undefined, organization_type_id: f.organizationTypeId || undefined }),
		},
		summary: { range: (v) => t("pagination.summary", v), empty: t("pagination.empty") },
	});
	const types = useQuery(organizationTypeOptionsOptions());
	const stats = useQuery(subAgencyStatsOptions());
	// Chip counts come from `/stats` — unfiltered totals, one aggregate statement.
	const typeOptions = organizationTypeChipOptions(types.data, stats.data?.by_organization_type, t("filters.noType"), search.organization_type_id);
	const total = page.list.data?.total;
	const sortValue = `${search.sort ?? "name"}:${search.dir ?? "asc"}`;
	const sortOptions = [
		{ value: "name:asc", label: tc("sort.nameAsc") },
		{ value: "name:desc", label: tc("sort.nameDesc") },
		{ value: "organization_type:asc", label: tc("sort.typeAsc") },
		{ value: "organization_type:desc", label: tc("sort.typeDesc") },
		{ value: "created_at:desc", label: tc("sort.newest") },
		{ value: "created_at:asc", label: tc("sort.oldest") },
	];
	const del = useConfirmDelete<SubAgencyListItem>({
		remove: (r) => deleteSubAgency(r.sub_agency_id),
		invalidate: invalidateSubAgencies,
		deleted: (r) => t("delete.deleted", { name: r.name }),
	});

	const columns: ColumnDef<SubAgencyListItem, SubAgencySortKey>[] = [
		{
			key: "name",
			header: t("table.name"),
			sortable: true,
			lockVisible: true,
			minWidth: "14rem",
			cell: (r) => <PrimaryCell>{r.name}</PrimaryCell>,
		},
		{
			key: "organization_type",
			header: t("table.type"),
			sortable: true,
			minWidth: "13rem",
			cell: (r) => (r.organization_type ? <ListTag>{r.organization_type.name}</ListTag> : <span className="text-base-content/30">—</span>),
		},
		{
			key: "sport_code" as SubAgencySortKey,
			header: t("table.sport"),
			minWidth: "6rem",
			cell: (r) => (r.sport_code ? <span className="font-mono text-[0.8125rem]">{r.sport_code}</span> : <span className="text-base-content/30">—</span>),
		},
		usageColumn("cards_count", t("table.cards"), t("table.cardsTitle")),
		usageColumn("scopes_count", t("table.scopes"), t("table.scopesTitle"), "7rem"),
		timestampColumn("created_at", t("table.created"), true),
	];

	return (
		<div className="flex min-w-0 flex-col gap-[1.125rem]">
			<ListHeader
				title={t("page.title")}
				count={total === undefined ? undefined : t("page.count", { count: total, n: formatNumber(total) })}
				description={<Trans t={t} i18nKey="page.intro" components={{ strong: <strong className="font-semibold text-gms-ink-2" /> }} />}
				actionLabel={t("page.addSubAgency")}
				onAction={isSuper ? () => setForm({ mode: "create" }) : undefined}
			/>

			<StatsStrip
				label={tc("list.summary")}
				cells={[
					{
						key: "total",
						label: t("stats.total"),
						value: total ?? "—",
						note: page.hasFilters ? t("stats.matching") : undefined,
					},
					...(stats.data
						? [
								{ key: "in-use", label: t("stats.inUse"), value: stats.data.in_use },
								{ key: "unused", label: t("stats.unused"), value: stats.data.unused },
							]
						: []),
				]}
			/>

			<ListToolbar
				search={search.search ?? ""}
				onSearch={(v) => page.patch({ search: v || undefined })}
				searchPlaceholder={t("filters.searchPlaceholder")}
				sort={{
					value: sortValue,
					options: sortOptions,
					onChange: (v) => {
						const [sort, dir] = v.split(":") as [SubAgencySortKey, "asc" | "desc"];
						page.patch({ sort, dir });
					},
				}}
				chips={[
					{
						kind: "options",
						key: "type",
						label: t("filters.typeChips"),
						options: typeOptions,
						value: search.organization_type_id ? [search.organization_type_id] : [],
						onChange: (v) => page.patch({ organization_type_id: v[0] ?? undefined }),
					},
				]}
				onClearAll={page.clearFilters}
			/>

			<DataTable<SubAgencyListItem, SubAgencySortKey>
				{...page.table}
				variant="list"
				columns={columns}
				rowKey={(r) => r.sub_agency_id}
				empty={page.emptyState({
					filtered: { title: t("empty.filteredTitle"), description: t("empty.filteredDescription") },
					none: { title: t("empty.noneTitle"), description: t("empty.noneDescription") },
					clearLabel: t("empty.clearFilters"),
				})}
				rowActions={
					isSuper
						? (r) => (
								<EditDeleteActions
									editLabel={t("table.editLabel", { name: r.name })}
									onEdit={() => setForm({ mode: "edit", subAgency: r })}
									deleteLabel={t("table.deleteLabel", { name: r.name })}
									onDelete={() => del.ask(r)}
									canDelete={r.cards_count === 0 && r.scopes_count === 0}
									variant="list"
								/>
							)
						: undefined
				}
			/>

			{isSuper && <SubAgencyFormModal target={form} onClose={() => setForm(null)} />}
			{isSuper && (
				<ConfirmDeleteModal
					{...del.modal}
					title={t("delete.title")}
					description={<Trans t={t} i18nKey="delete.description" values={{ name: del.row?.name ?? "" }} components={{ strong: <strong className="font-semibold text-base-content" /> }} />}
					confirmLabel={t("delete.confirm")}
					errorPrefix={t("delete.failed")}
				/>
			)}
		</div>
	);
}
